import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Alan,
  BosDurum,
  Buton,
  Iskelet,
  Kart,
  Rozet,
  SatirDugmesi,
  SatirMetni,
} from "@koclukpanel/ui";
import { ApiHatasi, personelListesi } from "../api";

/**
 * PERSONEL SEÇİCİ — Personel 360° için
 *
 * ================================================================
 * NEDEN AYRI BİR SEÇİCİ, NEDEN "Personel" EKRANI DEĞİL
 * ================================================================
 * "Personel" ekranı bir YÖNETİM listesi: rol atama, arşivleme, davet.
 * Burada aranan şey bir seçim. İkisini tek ekrana bindirseydik,
 * yalnızca birinin kaydına bakmak isteyen kullanıcı yönetim
 * eylemlerinin arasında kaybolurdu — ve o eylemler geri alınması zor
 * olanlar.
 *
 * Arşivlenmiş personel listede YOK: 360 ekranı "kimin kaydına
 * bakacağım" sorusunu cevaplıyor ve varsayılan cevap çalışanlar.
 */
export function PersonelSecici({ onSec }: { onSec: (id: string, ad: string) => void }) {
  const [arama, setArama] = useState("");

  const veri = useQuery({ queryKey: ["personel-listesi"], queryFn: () => personelListesi(false) });

  const suzulmus = useMemo(() => {
    const q = arama.trim().toLocaleLowerCase("tr-TR");
    const hepsi = veri.data?.personel ?? [];
    if (q === "") return hepsi;
    /*
      SÜZME İSTEMCİDE: liste zaten kapsam süzgecinden geçmiş ve en çok
      200 satır. Sunucuya arama eklemek her tuş vuruşunda bir ağ turu
      demek olurdu.
    */
    return hepsi.filter((p) =>
      `${p.firstName} ${p.lastName} ${p.email}`.toLocaleLowerCase("tr-TR").includes(q),
    );
  }, [veri.data, arama]);

  if (veri.isPending) return <Iskelet satir={5} />;

  if (veri.isError) {
    return (
      <BosDurum
        ton="hata"
        baslik="Personel listesi alınamadı"
        aciklama={veri.error instanceof ApiHatasi ? veri.error.message : undefined}
        eylem={
          <Buton gorunum="ikincil" onClick={() => void veri.refetch()}>
            Tekrar dene
          </Buton>
        }
      />
    );
  }

  return (
    <Kart
      baslik="Hangi personel?"
      aciklama="360° ekranı tek kişinin bütün kaydını tek yerde gösterir; önce kimin kaydına bakacağınızı seçin."
    >
      <div className="flex flex-col gap-3">
        <Alan
          etiket="Ara"
          yardim="Ad, soyad ya da e-posta."
          value={arama}
          onChange={(e) => setArama(e.target.value)}
        />

        {suzulmus.length === 0 ? (
          <p className="text-notr-700 text-sm">
            {arama.trim() ? "Bu aramaya uyan personel yok." : "Görüntülenebilecek personel yok."}
          </p>
        ) : (
          <ul className="flex flex-col gap-2">
            {suzulmus.map((p) => (
              <li key={p.id}>
                <SatirDugmesi onClick={() => onSec(p.id, `${p.firstName} ${p.lastName}`)}>
                  <SatirMetni baslik={`${p.firstName} ${p.lastName}`} altBaslik={p.email} />
                  {p.roller.length > 0 && <Rozet ton="notr">{p.roller[0]}</Rozet>}
                  {/* Yön işareti `aria-hidden`: düğmenin adı zaten başlık. */}
                  <span aria-hidden="true" className="text-notr-400 shrink-0">
                    ›
                  </span>
                </SatirDugmesi>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Kart>
  );
}
